import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { confirmationContext } from './confirmation';
import { ConfirmationDialog } from './ConfirmationDialog';
import { TConfirmationDialogProps, TConfirmationProps } from './types';

const getDefaultProps = (
  t: (t: string) => string
): TConfirmationDialogProps => ({
  title: t('common:confirmationDialogDefault.title'),
  description: t('common:confirmationDialogDefault.description'),
  rejectText: t('common:confirmationDialogDefault.rejectText'),
  confirmText: t('common:confirmationDialogDefault.confirmText'),
  isPending: false,
  shouldCloseOnConfirm: true,
  onClose: () => {},
  onConfirm: () => {},
});

export const ConfirmationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [dialogProps, setDialogProps] = useState<TConfirmationDialogProps>();
  const { t } = useTranslation();

  const contextValue = useMemo(() => {
    const closeConfirmation = () => {
      dialogProps?.onClose();
    };

    const getConfirmation = ({
      shouldCloseOnConfirm,
      ...otherProps
    }: TConfirmationProps = {}): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        setDialogProps({
          ...getDefaultProps(t),
          ...otherProps,
          onConfirm: () => {
            if (shouldCloseOnConfirm) {
              setDialogProps(undefined);
            }
            resolve(true);
          },
          onClose: () => {
            setDialogProps(undefined);
            resolve(false);
          },
        });
      });
    };

    const setPending = (isPending: boolean) => {
      setDialogProps((prevState) =>
        prevState ? { ...prevState, isPending } : prevState
      );
    };

    return { getConfirmation, closeConfirmation, setPending };
  }, [dialogProps, t]);

  return (
    <confirmationContext.Provider value={contextValue}>
      {children}
      {dialogProps && <ConfirmationDialog {...dialogProps} />}
    </confirmationContext.Provider>
  );
};
